/**
 * A9 (2026-06-04) — Atmosphere settings store.
 *
 * Single source of truth for every cinematic toggle introduced across
 * A1-A8. Persisted to localStorage so a user's choices survive page
 * reloads. The OS-level `prefers-reduced-motion` and headphones-out
 * overrides live in `useAtmosphereSettings` (a hook layered above this
 * store) — this store carries ONLY the user-controlled toggles.
 *
 * Defaults match the design doc §A9 toggle inventory: every cinematic
 * toggle defaults ON; `minimalMode` defaults OFF (it's an opt-in
 * preset that flips several toggles at once).
 *
 * Slice A scope:
 *   - All 13 toggles + minimal preset shipped in the store.
 *   - Subsequent sprints (A4/A5/A7/A8) will wire their components to
 *     the matching toggle key — no further store changes expected.
 *
 * NOTE on Zustand version: vibe-designer ships zustand ^4.5.0 (not
 * the ^5 declared in briefing-room-module's own package.json — see
 * the A2-deploy commit body for the dependency-resolution mechanics).
 * The uncurried `create<T>(...)` form used here is the v4 idiom but
 * is also valid in v5; the persist middleware shape matches v4 too.
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { DEFAULT_DICE_PALETTE, type DicePaletteId } from '../components/atmosphere/dicePalettes';
import { DEFAULT_DICE_THEME, type DiceThemeId } from '../components/atmosphere/diceThemes';

export interface AtmosphereSettings {
  // --- Master toggles (master switches OFF all under their group) ---
  /** Master animations toggle. Off => no framer-motion animations fire
   *  anywhere in the briefing-room cinematic surface. */
  animationsEnabled: boolean;
  /** Master SFX toggle. Off => AudioService.mute is set true. */
  sfxEnabled: boolean;

  // --- A1: gun-barrel + theme music (toggle-only; component TBD) ---
  gunBarrelOpenerEnabled: boolean;
  themeMusicTier: 'none' | 'stock' | 'user';

  // --- A2: dice tumble ---
  diceTumbleEnabled: boolean;
  voiceTumbleDifferentialEnabled: boolean;
  voiceConfirmStingEnabled: boolean;
  /** Dice tumble SFX peak volume (0.0–1.0). Selector mode reads this
   *  directly; voice mode adds a small boost on top. Persisted across
   *  sessions via the same localStorage payload as the toggles. */
  diceTumbleSfxVolume: number;

  // --- A3: QR cinematic responses ---
  qrCinematicsEnabled: boolean;
  qrBrassSwellEnabled: boolean;
  qrFlavorStringsEnabled: boolean;

  // --- A4: villain stings (toggle-only; component TBD) ---
  villainEntranceStingsEnabled: boolean;

  // --- A5: module skins (toggle-only; component TBD) ---
  moduleSkinsEnabled: boolean;

  // --- A6: hero point chip ---
  heroPointChipEnabled: boolean;

  // --- A7: Q Branch voice-over (toggle-only; component TBD) ---
  qBranchVoiceOverEnabled: boolean;

  // --- A8: dossier redaction (toggle-only; component TBD) ---
  dossierRedactionEnabled: boolean;

  // --- A8r Slice C (2026-06-04): Fame Recognition cinematic ---
  /** When true, the FameRecognitionCinematic overlay renders after a
   *  fresh fame roll (NEW rolls only — registry-load is always silent
   *  per useFameRecognition's freshlyRolledRef discipline). When false,
   *  recognition state still updates correctly but no visual flourish. */
  fameRecognitionCinematicEnabled: boolean;

  // --- A8r Slice D (2026-06-04): Manual recognition badge ---
  /** When true, NPC names whose recognition is currently GM-overridden
   *  render with a small "[GM]" badge for GM-facing transparency. When
   *  false, the badge is hidden but the underlying override state is
   *  unaffected — purely cosmetic toggle. */
  manualBadgeVisibleEnabled: boolean;

  // --- 2026-06-05 cinematic-linger: extended hold + manual dismiss ---
  /** How the QR + Fame Recognition cinematics dismiss after their reveal:
   *  - 'auto' — fire onComplete after `cinematicHoldMs` (the existing
   *             behavior, just with the duration now user-controlled).
   *  - 'manual' — stay on screen until the user clicks the backdrop OR
   *               the "Continue ▸" affordance. Each subsequent roll
   *               that fires while a cinematic is up is held in the
   *               queue managed by ScenarioRunner and dismissed in
   *               order.
   *  User feedback: "you need to LEAVE THEM ON THE SCREEN, as an
   *  optional setting, and default to show them for like 3-5 seconds,
   *  and if user sets 'leave until dismissed' they should need to be
   *  clicked to dismiss". */
  cinematicDismissMode: 'auto' | 'manual';
  /** Visible duration in 'auto' mode (ms). Applies to QR cinematic and
   *  the Fame Recognition reveal-hold phase. The per-QR defaults the
   *  cinematic used to use (QR1=3500, QR2=3000, QR3=2800, QR4=2500,
   *  Failure=3000) are now LOWER BOUNDS; the effective duration =
   *  max(per-QR-default, cinematicHoldMs). Default 4000ms (4s) matches
   *  the user's "3-5 seconds" guidance with a midpoint default. */
  cinematicHoldMs: number;

  // --- 2026-06-05 round 9 dice-feedback: percentile palette ---
  /** Color palette for the percentile-pair d10s (tens + ones). Registry
   *  lives in components/atmosphere/dicePalettes.ts; user selects via
   *  AtmosphereSettingsPanel. Defaults to 'bond' (gold + white). */
  dicePalette: DicePaletteId;

  // --- Round 14 (2026-06-06, Sprint 2 §4.3) dice chip fly ---
  /** When true, generic (Save / Damage / Skill Check) roll chips
   *  animate from the tumble overlay to the History drawer toggle button
   *  after settle — a "cinematic" alternative to the static chip fade
   *  for generic rolls. Per user choice 2026-06-06: percentile rolls
   *  SKIP this animation (the QR cinematic card is already their
   *  visual reveal). Falls back to no-op when animationsEnabled is OFF
   *  per the master switch. */
  diceChipFlyEnabled: boolean;

  // --- Sprint 3 §4.2 (2026-06-06): dice-box theme substrate ---
  /** dice-box theme directory under /assets/dice-box/themes/. Registry
   *  lives in components/atmosphere/diceThemes.ts; user selects via
   *  AtmosphereSettingsPanel. Composes with `dicePalette`: theme swaps
   *  the underlying texture; palette tints via themeColor at roll() time.
   *  Defaults to 'default' (white plastic substrate). */
  diceTheme: DiceThemeId;

  // --- Sprint 4 §4.5 (2026-06-06): voice transcript debug surface ---
  /** When true, ScenarioRunner renders a small voice-debug control
   *  (textarea + Send button) that calls `dispatcher.dispatchTranscript`
   *  with the textarea contents. Used by the
   *  briefing-room-voice-force-dice-roll Playwright spec to inject a
   *  transcript WITHOUT going through the mic / STT layer. Default OFF;
   *  user opts in via AtmosphereSettingsPanel. Mocked-route Playwright
   *  tests can also fill localStorage directly to bypass the toggle UI. */
  voiceDebugEnabled: boolean;
}

export interface AtmosphereSettingsActions {
  setSetting: <K extends keyof AtmosphereSettings>(
    key: K,
    value: AtmosphereSettings[K],
  ) => void;
  applyMinimalPreset: () => void;
  resetToDefaults: () => void;
}

export type AtmosphereSettingsStore = AtmosphereSettings & AtmosphereSettingsActions;

export const DEFAULT_SETTINGS: AtmosphereSettings = {
  animationsEnabled: true,
  sfxEnabled: true,
  gunBarrelOpenerEnabled: true,
  themeMusicTier: 'none',
  diceTumbleEnabled: true,
  voiceTumbleDifferentialEnabled: true,
  voiceConfirmStingEnabled: true,
  diceTumbleSfxVolume: 0.3,
  qrCinematicsEnabled: true,
  qrBrassSwellEnabled: true,
  qrFlavorStringsEnabled: true,
  villainEntranceStingsEnabled: true,
  moduleSkinsEnabled: true,
  heroPointChipEnabled: true,
  qBranchVoiceOverEnabled: true,
  dossierRedactionEnabled: true,
  fameRecognitionCinematicEnabled: true,
  manualBadgeVisibleEnabled: true,
  cinematicDismissMode: 'auto',
  cinematicHoldMs: 4000,
  dicePalette: DEFAULT_DICE_PALETTE,
  diceChipFlyEnabled: true,
  diceTheme: DEFAULT_DICE_THEME,
  voiceDebugEnabled: false,
};

/**
 * Minimal mode preset — disables visual cinematics and SFX-heavy
 * features. Designed for users who want the gameplay loop to keep
 * working but cinematic affordances to step out of the way.
 *
 * Master `animationsEnabled` + `sfxEnabled` flip OFF; individual
 * toggles are also flipped to OFF for legibility in the settings UI
 * (so the user sees the resulting state explicitly).
 */
export const MINIMAL_PRESET: AtmosphereSettings = {
  ...DEFAULT_SETTINGS,
  animationsEnabled: false,
  sfxEnabled: false,
  gunBarrelOpenerEnabled: false,
  themeMusicTier: 'none',
  diceTumbleEnabled: false,
  voiceTumbleDifferentialEnabled: false,
  voiceConfirmStingEnabled: false,
  diceTumbleSfxVolume: 0,
  qrCinematicsEnabled: false,
  qrBrassSwellEnabled: false,
  // QR flavor strings stay ON in minimal mode — they're text content,
  // not a cinematic affordance, and the GM may still want narrative
  // flavor on the result panel.
  qrFlavorStringsEnabled: true,
  villainEntranceStingsEnabled: false,
  moduleSkinsEnabled: false,
  heroPointChipEnabled: false,
  qBranchVoiceOverEnabled: false,
  // Dossier redaction is a security feature — leave ON in minimal mode.
  dossierRedactionEnabled: true,
  // A8r Slice C: cinematic is animation-heavy — flips OFF with minimal preset.
  fameRecognitionCinematicEnabled: false,
  // A8r Slice D: manual badge is informational, NOT a cinematic affordance.
  // Stay ON in minimal mode — it's a security/transparency feature like
  // dossierRedactionEnabled.
  manualBadgeVisibleEnabled: true,
  // Cinematic dismiss settings: minimal mode keeps 'auto' but shortens
  // hold to the lowest acceptable value (1500ms — under 3s is too short
  // to read but under 1500ms reads as "didn't fire" per the original
  // 2026-06-04 dice-feedback bug). Manual mode is opt-in only — minimal
  // users want LESS friction, not more clicks to dismiss.
  cinematicDismissMode: 'auto',
  cinematicHoldMs: 1500,
  dicePalette: DEFAULT_DICE_PALETTE,
  // Round 14: chip-fly is animation-heavy, flips OFF with minimal preset.
  diceChipFlyEnabled: false,
  // Sprint 3 §4.2: minimal mode stays on the default substrate — themes
  // are an aesthetic affordance, not a friction-reducer like the others.
  diceTheme: DEFAULT_DICE_THEME,
  // Sprint 4 §4.5: voice debug is a developer/test affordance — OFF in
  // every preset including minimal. A user in minimal mode wants LESS
  // surface, not a debug control they didn't ask for.
  voiceDebugEnabled: false,
};

const STORAGE_KEY = 'br-atmosphere-settings-v1';

export const useAtmosphereSettingsStore = create<AtmosphereSettingsStore>()(
  persist(
    (set) => ({
      ...DEFAULT_SETTINGS,
      setSetting: (key, value) => set({ [key]: value } as Partial<AtmosphereSettings>),
      applyMinimalPreset: () => set({ ...MINIMAL_PRESET }),
      resetToDefaults: () => set({ ...DEFAULT_SETTINGS }),
    }),
    {
      name: STORAGE_KEY,
      // Persist only the settings — not the action functions (those are
      // closures; persisting them would be a no-op anyway, but
      // explicit partialize keeps the storage payload minimal).
      partialize: (state) => {
        const { setSetting: _s, applyMinimalPreset: _a, resetToDefaults: _r, ...rest } = state;
        void _s;
        void _a;
        void _r;
        return rest;
      },
    },
  ),
);

/**
 * Reset helper for tests. Restores the store to default settings AND
 * clears the persisted localStorage entry so subsequent tests start
 * from a clean slate.
 */
export function resetAtmosphereSettingsForTests(): void {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY);
  }
  useAtmosphereSettingsStore.setState({ ...DEFAULT_SETTINGS });
}
